import { useMemo } from "react"
import TooltipIcon from "@/v2-ui/tooltip/TooltipIcon"
import { useTableContext } from "./table.context"
import { getColumnTitle } from "./table.utils"
import type { ColumnFilterConfig } from "./table.types"
import TableFilterInput from "./filter/TableFilterInput"

/**
 * Renders filter inputs for columns marked with `filter.toolbar: true`
 * directly in the toolbar row.
 */
function TableToolbarFilters() {
  const { table } = useTableContext()

  // Keyed on the column list, not the table instance: TanStack rebuilds the
  // columns when the definitions change (a facet whose options arrive from a
  // lookup query), and a list memoised on the table alone would keep
  // rendering the first definitions' options.
  const allColumns = table.getAllColumns()
  const toolbarColumns = useMemo(() => {
    return allColumns.filter((column) => {
      if (column.id === "__select") return false
      const meta = column.columnDef.meta as
        | { filter?: ColumnFilterConfig }
        | undefined
      return !!meta?.filter?.toolbar
    })
  }, [allColumns])

  if (toolbarColumns.length === 0) return null

  return (
    <>
      {toolbarColumns.map((column) => {
        const meta = column.columnDef.meta as {
          filter: ColumnFilterConfig
          description?: string | null
        }
        const title = getColumnTitle(column)

        return (
          <div key={column.id} className="ui-table-toolbar-filter">
            <label className="ui-table-toolbar-filter-label">
              {title}
              {meta.description ? (
                <TooltipIcon
                  iconSrc="info"
                  iconSize={12}
                  iconClassName="ui-table-header-tooltip-icon"
                  content={meta.description}
                  contentSide="top"
                  contentClassName="ui-tooltip-content--panel max-w-[280px]"
                />
              ) : null}
            </label>
            <TableFilterInput
              filter={meta.filter}
              value={column.getFilterValue()}
              onChange={(value) => {
                column.setFilterValue(value)
              }}
            />
          </div>
        )
      })}
    </>
  )
}

export default TableToolbarFilters
